import React from 'react';
import { useSelector } from 'react-redux';
import {
  defaultDirection,
  defaultLocale,
  defaultColor,
  localeOptions,
  themeColorStorageKey,
  themeRadiusStorageKey,
  generalFormatDate,
  leaveRequestActionErrorCodes,
  commonActionErrorCodes,
  remoteWorkRequestActionErrorCodes,
  permissionRequestActionErrorCodes,
  generalFormatDateForTableDate,
} from '../constants/defaultValues';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import { clearAuth } from '@/redux/auth/user-slice';
import { clearLoggedInEmployee } from '@/redux/employee/logged-in-employee-slice';
import { WeekDays } from '@/constants/defaultValues';
import { resolveMenuConfig } from '@/components/sidebarConfigByCode';
import { getApiBaseUrl } from '@/constants/apiConfig';
import api from '@/api/api';

import { constant } from 'lodash';
let __configPromise;

const resolveConfigPath = () => {
  // Use a root-relative path to avoid subdirectory edge cases
  return `/config.json`;
};

export const getConfig = async () => {
  try {
    if (!__configPromise) {
      const configPath = resolveConfigPath();
      __configPromise = fetch(configPath)
        .then((r) => {
          if (!r.ok) throw new Error('Failed to fetch configuration');
          return r.json();
        })
        .catch((e) => {
          __configPromise = undefined; // allow retry on failure
          throw e;
        });
    }
    return await __configPromise;
  } catch (error) {
    console.error('Error loading configuration:', error);
  }
};

export const getApiPath = async () => {
  try {
    const data = await getConfig();
    return data?.apiPath;
  } catch (error) {
    console.error('Error loading configuration:', error);
  }
};
export const getLoginApiPath = async () => {
  try {
    const data = await getConfig();
    return data?.loginApiPath;
  } catch (error) {
    console.error('Error loading configuration:', error);
  }
};

export const toDisplayImageUrl = async (raw) => {
  if (!raw) return '';
  const s = String(raw).replace(/\\+/g, '/');
  if (/^https?:\/\//i.test(s) || s.startsWith('data:')) return s;

  // Prefer axios instance baseURL, fallback to config helper
  const base =
    api?.defaults?.baseURL ||
    (typeof getApiBaseUrl === 'function' ? getApiBaseUrl() || '' : '');

  const cleaned = s.replace(/^\/+/, '');
  const path = `OrganizationService/api/File/get?path=${encodeURI(cleaned)}`;

  // Fetch through axios so interceptors add auth headers; return an object URL
  try {
    const resp = await api.get(path, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(resp.data);
    return blobUrl;
  } catch (error) {
    console.error('toDisplayImageUrl failed; falling back to direct URL', error);
    return `${base}${path}`;
  }
};

// Icon paths under the legacy AddonImages/ folder no longer exist on the file
// server, so resolving them always produces a failing File/get call. Skip the
// request entirely and let callers fall back to their built-in icons.
export const shouldResolveImageUrl = (raw) => {
  const s = String(raw || '')
    .trim()
    .replace(/\\+/g, '/')
    .replace(/^\/+/, '');
  if (!s) return false;
  if (/^https?:\/\//i.test(s) || s.startsWith('data:')) return true;
  return !/^addonimages\//i.test(s);
};

// Function to sanitize HTML content
export function sanitizeHTML(html) {
  // Define sanitization options
  const options = {
    // Allow almost all tags
    allowedTags: sanitizeHtml.defaults.allowedTags.concat([
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'hr',
      'img',
      'video',
      'audio',
      'iframe',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'pre',
      'code',
      'div',
    ]),
    // Allow almost all attributes
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      '*': ['style', 'class', 'id'],
      a: ['href', 'name', 'target'],
      img: ['src', 'alt'],
      video: ['src', 'poster', 'controls'],
      audio: ['src', 'controls'],
      iframe: ['src', 'allow', 'allowfullscreen', 'width', 'height'],
      table: ['border', 'cellpadding', 'cellspacing'],
      th: ['colspan', 'rowspan'],
      td: ['colspan', 'rowspan'],
    },
    // Allow data URI for images and links
    allowedSchemes: sanitizeHtml.defaults.allowedSchemes.concat(['data']),
    // Allow HTML comments
    allowComments: true,
    // Treat unknown protocols as 'http' or 'https'
    enforceHtmlBoundary: true,
  };

  // Sanitize HTML content
  return sanitizeHtml(html, options);
}

export const mapOrder = (array, order, key) => {
  // eslint-disable-next-line func-names
  array.sort(function (a, b) {
    const A = a[key];
    const B = b[key];
    if (order.indexOf(`${A}`) > order.indexOf(`${B}`)) {
      return 1;
    }
    return -1;
  });
  return array;
};

export const getDateWithFormat = () => {
  const today = new Date();
  let dd = today.getDate();
  let mm = today.getMonth() + 1; // January is 0!

  const yyyy = today.getFullYear();
  if (dd < 10) {
    dd = `0${dd}`;
  }
  if (mm < 10) {
    mm = `0${mm}`;
  }
  return `${yyyy}-${mm}-${dd}`;
};

export const getCurrentTime = () => {
  const now = new Date();
  return `${now.getHours()}:${now.getMinutes()}`;
};

// Shared regex patterns for validations
export const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
export const MOBILE_REGEX = /^\+?\d{8,15}$/;
export const ARABIC_TEXT_REGEX = /^[\u0600-\u06FF\u0750-\u077F\u0660-\u0669A-Za-z\d\s\-'.,]+$/;
export const URL_REGEX = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;

// Mirrors backend enum ReportNotificationType (Notification Type report selection)
export const ReportNotificationType = {
  None: 0,
  Attendance: 1,
  Violation: 2,
  EmployeeRequest: 3,
  CustomReport: 4,
  ContinousAbsent: 5,
};

// Mirrors backend enum NotificationAttachmentFormatEnum
export const NotificationAttachmentFormatEnum = {
  None: 0,
  Excel: 1,
  Html: 2,
  HtmlInBody: 4,
  ExcelHtml: 3,
  ExcelHtmlInBody: 5,
  HtmlHtmlInBody: 6,
  All: 7,
};

// Mirrors backend enum NotificationScopeEnum (Notification Type scope selection)
export const NotificationScopeEnum = {
  None: 0,
  Employee: 1,
  Manager: 2,
  Company: 3,
};

export const getDirection = () => {
  let direction = defaultDirection;

  try {
    if (localStorage.getItem('direction')) {
      const localValue = localStorage.getItem('direction');
      if (localValue === 'rtl' || localValue === 'ltr') {
        direction = localValue;
      }
    }
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : getDirection -> error', error);
    direction = defaultDirection;
  }
  return {
    direction,
    isRtl: direction === 'rtl',
  };
};
export const setDirection = (localValue) => {
  let direction = 'ltr';
  if (localValue === 'rtl' || localValue === 'ltr') {
    direction = localValue;
  }
  try {
    localStorage.setItem('direction', direction);
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : setDirection -> error', error);
  }
};

export const getCurrentColor = () => {
  let currentColor = defaultColor;
  try {
    if (localStorage.getItem(themeColorStorageKey)) {
      currentColor = localStorage.getItem(themeColorStorageKey);
    }
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : getCurrentColor -> error', error);
    currentColor = defaultColor;
  }
  return currentColor;
};

export const setCurrentColor = (color) => {
  try {
    localStorage.setItem(themeColorStorageKey, color);
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : setCurrentColor -> error', error);
  }
};

export const setLoginProcess = (loginCriteria) => {
  try {
    localStorage.setItem('LoginCriteria', loginCriteria);
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : setLoginProcess -> error', error);
  }
};
export const getLoginProcess = () => {
  try {
    return localStorage.getItem('LoginCriteria');
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : getLoginProcess -> error', error);
  }
};

export const showError = (message, color) => {
  const event = new CustomEvent('show-alert', {
    detail: {
      message,
      color,
    },
  });
  window.dispatchEvent(event);
};

export const openFileInNewTab = async (data) => {
  try {
    console.log('openFileInNewTab call');
    if (!data || !data.data) {
      const msg = getLabel('لم يتم العثور على بيانات', 'No Data Found');
      showError(msg, 'warning');
      return;
    }

    const fileData = data.data;
    const contentType = data.headers?.['content-type'] || 'application/pdf';

    // Avoid copying large blobs in memory – reuse the Blob when possible
    const blob = fileData instanceof Blob ? fileData : new Blob([fileData], { type: contentType });

    // Very large HTML/PDFs make the browser print preview extremely slow.
    // For big files, download instead of opening the print UI to keep the UI responsive.
    const MAX_INLINE_PREVIEW_SIZE = 15 * 1024 * 1024; // 15 MB
    if (blob.size > MAX_INLINE_PREVIEW_SIZE) {
      downloadFile({
        ...data,
        data: blob,
        headers: {
          ...(data.headers || {}),
          'content-type': contentType,
        },
      });
      return;
    }

    const url = window.URL.createObjectURL(blob);

    // IMPORTANT: open with `noopener`.
    // Without it the new tab keeps a reference to this window (window.opener) and the browser
    // places BOTH tabs in the SAME renderer process. Any heavy work in the report tab
    // (laying out a huge HTML table, print preview) then blocks this tab's main thread,
    // which is why the app froze when switching back. `noopener` breaks that link so the
    // report tab gets its own process and cannot block the application UI.
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoke the URL later to free memory
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 120000); // 2 minutes – enough time for large files to load
  } catch (error) {
    console.log(error);
  }
};

export const downloadFile = async (data) => {
  try {
    console.log('downloadFile call');
    if (!data || !data.data) {
      const msg = getLabel('لم يتم العثور على بيانات', 'No Data Found');
      showError(msg, 'warning');
      return;
    }

    const fileData = data.data;
    const contentType = data.headers?.['content-type'] || 'application/octet-stream';
    const blob = fileData instanceof Blob ? fileData : new Blob([fileData], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const fileName = data.headers?.['x-file-name'] || 'Report';

    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.log(error);
  }
};

export const getCurrentRadius = () => {
  let currentRadius = 'rounded';
  try {
    if (localStorage.getItem(themeRadiusStorageKey)) {
      currentRadius = localStorage.getItem(themeRadiusStorageKey);
    }
  } catch (error) {
    console.log(
      '>>>>: src/helpers/Utils.js : getCurrentRadius -> error',
      error
    );
    currentRadius = 'rounded';
  }
  return currentRadius;
};

export const setCurrentRadius = (radius) => {
  try {
    localStorage.setItem(themeRadiusStorageKey, radius);
  } catch (error) {
    console.log(
      '>>>>: src/helpers/Utils.js : setCurrentRadius -> error',
      error
    );
  }
};

// Default employee-level UI configuration
export const defaultEmployeeConfig = {
  theme: defaultColor,
  language: defaultLocale,
  paginationRows: 10,
  leftMenu: 'Fixed',
};

// Read employee configuration from localStorage with safe fallbacks
export const getEmployeeConfigFromStorage = () => {
  try {
    const storedTheme = localStorage.getItem('PreferredTheme');
    const storedLang = localStorage.getItem('PreferredLanguage');
    const storedRows = parseInt(
      localStorage.getItem('PreferredPaginationRows') || '',
      10
    );
    const storedLeftMenu = localStorage.getItem('PreferredLeftMenue');

    const theme = storedTheme || defaultEmployeeConfig.theme;

    // Fall back to the runtime language key when PreferredLanguage is not yet written
    const langSource = storedLang || localStorage.getItem('currentLanguage');

    let language = defaultEmployeeConfig.language;
    if (langSource) {
      const v = String(langSource).toLowerCase();
      language = v.startsWith('ar') ? 'ar' : 'en';
    }

    const paginationRows = Number.isNaN(storedRows)
      ? defaultEmployeeConfig.paginationRows
      : storedRows;

    const leftMenu = storedLeftMenu || defaultEmployeeConfig.leftMenu;

    return {
      theme,
      language,
      paginationRows,
      leftMenu,
    };
  } catch (error) {
    console.error('Error in getEmployeeConfigFromStorage:', error);
    return { ...defaultEmployeeConfig };
  }
};

// Persist employee configuration to localStorage and apply side effects
export const saveEmployeeConfigToStorage = (config) => {
  if (!config) return defaultEmployeeConfig;

  try {
    const merged = {
      ...defaultEmployeeConfig,
      ...config,
    };

    if (merged.theme) {
      localStorage.setItem('PreferredTheme', merged.theme);
      setCurrentColor(merged.theme);
    }

    if (merged.language) {
      const lang = merged.language === 'ar' ? 'ar' : 'en';
      localStorage.setItem('PreferredLanguage', lang);
      setCurrentLanguage(lang);
    }

    if (merged.paginationRows) {
      localStorage.setItem(
        'PreferredPaginationRows',
        String(merged.paginationRows)
      );
    }

    if (merged.leftMenu) {
      localStorage.setItem('PreferredLeftMenue', merged.leftMenu);
    }

    return merged;
  } catch (error) {
    console.error('Error in saveEmployeeConfigToStorage:', error);
    return { ...defaultEmployeeConfig };
  }
};

// Reset stored employee configuration back to defaults
export const resetEmployeeConfigToDefault = () => {
  return saveEmployeeConfigToStorage(defaultEmployeeConfig);
};

export const getLabel = (itemAr, itemEn, entity) => {
  if (getCurrentLanguage() === 'ar') return itemAr;
  else return itemEn;
};

export const getCurrentLanguage = () => {
  let language = defaultLocale;
  try {
    const storedLanguage = localStorage.getItem('currentLanguage');
    if (
      storedLanguage &&
      localeOptions.some((locale) => locale.id === storedLanguage)
    ) {
      language = storedLanguage;
    }
  } catch (error) {
    console.error('Error in getCurrentLanguage:', error);
  }
  return language;
};

export const setCurrentLanguage = (locale) => {
  try {
    if (localeOptions.some((localeOption) => localeOption.id === locale)) {
      localStorage.setItem('currentLanguage', locale);
    } else {
      console.warn(`Invalid locale provided: ${locale}`);
    }
  } catch (error) {
    console.error('Error in setCurrentLanguage:', error);
  }
};

export const removeCurrentUser = () => {
  localStorage.removeItem('currentUser');
};

export const getCurrentUser = () => {
  try {
    const user = localStorage.getItem('currentUser');
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error('Error in getCurrentUser:', error);
    return null;
  }
};

export const patchCurrentUserToLocalStorage = (patch) => {
  try {
    const current = getCurrentUser() || {};
    const next = {
      ...(current || {}),
      ...(patch || {}),
    };
    localStorage.setItem('currentUser', JSON.stringify(next));
    return next;
  } catch (error) {
    console.error('Error in patchCurrentUserToLocalStorage:', error);
    return null;
  }
};

export const Logout = (dispatch) => {
  // Clear old localStorage keys
  localStorage.removeItem('current_user');
  localStorage.removeItem('user_privileges');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('access_token');
  
  localStorage.removeItem('currentUser');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('accessTokenExpiresAt');
  localStorage.removeItem('refreshTokenExpiresAt');

  // Clear employee-specific cached data
  localStorage.removeItem('employeeInfo');
  localStorage.removeItem('employeeConfiguration');
  localStorage.removeItem('shortcuts');
  localStorage.removeItem('companyID');
  localStorage.removeItem('assignRoleCompanyID');
  localStorage.removeItem('assignRoleCompanyName');
  localStorage.removeItem('assignRoleID');
  localStorage.removeItem('assignRoleKey');

  // Clear employee UI configuration preferences
  localStorage.removeItem('PreferredTheme');
  localStorage.removeItem('PreferredLanguage');
  localStorage.removeItem('PreferredPaginationRows');
  localStorage.removeItem('PreferredLeftMenue');

  // Language mirrors of PreferredLanguage so the next user does not inherit it
  localStorage.removeItem('currentLanguage');

  dispatch(clearLoggedInEmployee());
  dispatch(clearAuth());
};

export const updateLocalAccessToken = (token) => {
  try {
    const user = getCurrentUser();
    if (user) {
      user.token = token;
      localStorage.setItem('currentUser', JSON.stringify(user));
    } else {
      console.warn('No current user found to update token.');
    }
  } catch (error) {
    console.error('Error in updateLocalAccessToken:', error);
  }
};

export const saveTokensToLocalStorage = async (user) => {
  if (!user?.accessToken) return;

  // Support both new (employeeInfo) and legacy (employeeDetails) shapes from
  // login / refresh responses.
  const employeeInfo = user?.employeeInfo || user?.employeeDetails || {};
  const shortcuts = user?.shortcuts || [];

  const existingCurrentUser = getCurrentUser() || {};

  const coerceBoolean = (value, fallback = false) => {
    if (value === undefined || value === null) return fallback;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
      if (['false', '0', 'no', 'n'].includes(normalized)) return false;
    }
    return fallback;
  };

  const resolveAssignRoleKeyFromResponse = () => {
    if (!Array.isArray(user?.assignRoles) || user.assignRoles.length === 0) {
      return null;
    }

    const selected = user.assignRoles.find((item) => coerceBoolean(item?.isSelected, false));
    const first = user.assignRoles[0];
    const candidate =
      selected?.assignRolekey ??
      selected?.assignRoleKey ??
      selected?.assignRoleID ??
      first?.assignRolekey ??
      first?.assignRoleKey ??
      first?.assignRoleID ??
      null;

    return candidate ? String(candidate) : null;
  };

  const resolveRoleNameFromResponse = () => {
    if (!Array.isArray(user?.assignRoles) || user.assignRoles.length === 0) {
      return (user?.role || '').trim?.() || employeeInfo?.designation;
    }
    
    const selected = user.assignRoles.find((item) => coerceBoolean(item?.isSelected, false));
    const first = user.assignRoles[0];
    const candidate = selected?.roleName ?? first?.roleName ?? null;
    
    return candidate ? String(candidate).trim() : (user?.role || '').trim?.() || employeeInfo?.designation;
  };

  // Configuration can arrive either at the root (employeeConfigration) or per assigned role
  const resolveEmployeeConfigurationFromResponse = () => {
    const rootConfig = Array.isArray(user?.employeeConfigration)
      ? user.employeeConfigration
      : Array.isArray(user?.employeeConfiguration)
      ? user.employeeConfiguration
      : [];

    let roleConfig = [];
    if (Array.isArray(user?.assignRoles) && user.assignRoles.length > 0) {
      const selectedRole =
        user.assignRoles.find((item) => coerceBoolean(item?.isSelected, false)) ||
        user.assignRoles[0];
      const candidate =
        selectedRole?.employeeConfiguration ?? selectedRole?.employeeConfigration;
      if (Array.isArray(candidate)) roleConfig = candidate;
    }

    // Role-level values win over root-level values for the same property
    const merged = new Map();
    [...rootConfig, ...roleConfig].forEach((cfg) => {
      const property = cfg?.property || cfg?.Property;
      if (!property) return;
      merged.set(property, {
        ...cfg,
        property,
        propertyValue: cfg?.propertyValue ?? cfg?.PropertyValue,
      });
    });

    return Array.from(merged.values());
  };

  const employeeConfiguration = resolveEmployeeConfigurationFromResponse();

  const assignRoleKeyFromResponse = resolveAssignRoleKeyFromResponse();
  const roleNameFromResponse = resolveRoleNameFromResponse();

  const resolvedNameAr =
    user?.nameAr ||
    user?.fullNameAr ||
    user?.nameArabic ||
    employeeInfo?.nameAr ||
    employeeInfo?.fullNameAr ||
    employeeInfo?.nameArabic ||
    '';

  const resolvedNameEn =
    user?.nameEn ||
    user?.fullNameEn ||
    user?.nameEnglish ||
    employeeInfo?.nameEn ||
    employeeInfo?.fullNameEn ||
    employeeInfo?.nameEnglish ||
    employeeInfo?.name ||
    '';

  const existingEmployeeInfo = existingCurrentUser?.employeeInfo || {};
  const mergedManagerInfo = {
    ...(existingEmployeeInfo?.manager || {}),
    ...(employeeInfo?.manager || {}),
  };

  const mergedEmployeeInfo = {
    ...existingEmployeeInfo,
    ...employeeInfo,
    manager: mergedManagerInfo,
  };

  const currentUser = {
    ...(existingCurrentUser || {}),
    // Legacy fields used across the app
    userName: user?.userName || employeeInfo?.email || employeeInfo?.employeeNo,
    id: employeeInfo?.id || user?.id,
    employeeNo: employeeInfo?.employeeNo || user?.employeeNo,
    nameAr: resolvedNameAr,
    nameEn: resolvedNameEn,
    companyID: employeeInfo?.companyID || user?.companyID,
    managerID: employeeInfo?.manager?.id || user?.managerID,
    employeeImage: employeeInfo?.image || user?.employeeImage,
    managerImage: user?.managerImage,
    managerNo: employeeInfo?.manager?.employeeNo || user?.managerNo,
    managerNameAr: employeeInfo?.manager?.nameAr || user?.managerNameAr || existingCurrentUser?.managerNameAr,
    managerNameEn: employeeInfo?.manager?.nameEn || employeeInfo?.manager?.name || user?.managerNameEn || existingCurrentUser?.managerNameEn,
    role: roleNameFromResponse,
    isManager: coerceBoolean(employeeInfo?.isManager ?? user?.isManager, false),
    normalUser: coerceBoolean(employeeInfo?.isNormalEmployee ?? employeeInfo?.normalUser ?? user?.normalUser, false),
    isMultiRole: coerceBoolean(employeeInfo?.isMultiRole ?? employeeInfo?.IsMultiRole ?? user?.isMultiRole ?? user?.IsMultiRole, false),
    groupIds: user?.groupIds,
    userTypeID: user?.userTypeID,
    isHr: user?.isHr,
    isSecurity: user?.isSecurity,
    lang: user?.lang,
    isEntityPrivilege: user?.isEntityPrivilege,
    
    // Add assignRoles array for role display in AppLayout
    assignRoles: user?.assignRoles || [],

    // Fields mirroring employeeInfo shape for dashboards and new code
    name: employeeInfo?.name || existingCurrentUser?.name,
    designation: employeeInfo?.designation || employeeInfo?.designationNameEn || existingCurrentUser?.designation,
    email: employeeInfo?.email || existingCurrentUser?.email,
    location: employeeInfo?.location || employeeInfo?.locationNameEn || existingCurrentUser?.location,
    department: employeeInfo?.department || employeeInfo?.departmentNameEn || existingCurrentUser?.department,
    category: employeeInfo?.category || employeeInfo?.categoryNameEn || existingCurrentUser?.category,
    mobileNo: employeeInfo?.mobileNo ?? existingCurrentUser?.mobileNo,
    status: employeeInfo?.status || employeeInfo?.statusNameEn || existingCurrentUser?.status,
    image: employeeInfo?.image || existingCurrentUser?.image,
    manager: Object.keys(mergedManagerInfo).length > 0 ? mergedManagerInfo : null,

    // Full bilingual employeeInfo fields from latest login response
    companyNameAr: employeeInfo?.companyNameAr || existingCurrentUser?.companyNameAr,
    companyNameEn: employeeInfo?.companyNameEn || existingCurrentUser?.companyNameEn,
    locationNameAr: employeeInfo?.locationNameAr || existingCurrentUser?.locationNameAr,
    locationNameEn: employeeInfo?.locationNameEn || existingCurrentUser?.locationNameEn,
    categoryNameAr: employeeInfo?.categoryNameAr || existingCurrentUser?.categoryNameAr,
    categoryNameEn: employeeInfo?.categoryNameEn || existingCurrentUser?.categoryNameEn,
    statusNameAr: employeeInfo?.statusNameAr || existingCurrentUser?.statusNameAr,
    statusNameEn: employeeInfo?.statusNameEn || existingCurrentUser?.statusNameEn,
    departmentNameAr: employeeInfo?.departmentNameAr || existingCurrentUser?.departmentNameAr,
    departmentNameEn: employeeInfo?.departmentNameEn || existingCurrentUser?.departmentNameEn,
    designationNameAr: employeeInfo?.designationNameAr || existingCurrentUser?.designationNameAr,
    designationNameEn: employeeInfo?.designationNameEn || existingCurrentUser?.designationNameEn,
    companyName: employeeInfo?.companyNameEn || employeeInfo?.companyNameAr || existingCurrentUser?.companyName,
    statusName: employeeInfo?.statusNameEn || employeeInfo?.statusNameAr || existingCurrentUser?.statusName,

    // Manager bilingual details
    managerDepartmentNameAr: employeeInfo?.manager?.departmentNameAr || existingCurrentUser?.managerDepartmentNameAr,
    managerDepartmentNameEn: employeeInfo?.manager?.departmentNameEn || existingCurrentUser?.managerDepartmentNameEn,
    managerDesignationNameAr: employeeInfo?.manager?.designationNameAr || existingCurrentUser?.managerDesignationNameAr,
    managerDesignationNameEn: employeeInfo?.manager?.designationNameEn || existingCurrentUser?.managerDesignationNameEn,
    managerEmail: employeeInfo?.manager?.email || existingCurrentUser?.managerEmail,
    managerMobileNo: employeeInfo?.manager?.mobileNo || existingCurrentUser?.managerMobileNo,
    managerEmployeeImage: employeeInfo?.manager?.image || existingCurrentUser?.managerEmployeeImage,
  };

  try {
    localStorage.removeItem('assignRoleCompanyID');
    localStorage.removeItem('assignRoleCompanyName');
    localStorage.removeItem('assignRoleKey');
    localStorage.removeItem('assignRoleID');
    localStorage.removeItem('userRolePrivileges');
    localStorage.removeItem('companyID');

    localStorage.setItem('currentUser', JSON.stringify(currentUser));

    if (employeeInfo?.companyID) {
      localStorage.setItem('companyID', employeeInfo.companyID);
    }

    if (assignRoleKeyFromResponse) {
      localStorage.setItem('assignRoleKey', assignRoleKeyFromResponse);
    }

    // Persist role ID for privilege/role-based APIs
    if (employeeInfo?.roleID) {
      try {
        localStorage.setItem('userRolePrivileges', employeeInfo.roleID);
      } catch (e) {
        console.error('Error saving userRolePrivileges:', e);
      }
    }

    // Always clear old employee-specific config before saving new data
    localStorage.removeItem('employeeInfo');
    localStorage.removeItem('employeeConfiguration');
    localStorage.removeItem('shortcuts');

    if (employeeInfo && Object.keys(employeeInfo).length > 0) {
      localStorage.setItem('employeeInfo', JSON.stringify(employeeInfo));
    }

    if (employeeConfiguration && employeeConfiguration.length > 0) {
      localStorage.setItem(
        'employeeConfiguration',
        JSON.stringify(employeeConfiguration)
      );
    }

    if (shortcuts && shortcuts.length > 0) {
      localStorage.setItem('shortcuts', JSON.stringify(shortcuts));
    }

    // tokens under both old and new keys for backward compatibility
    localStorage.setItem('accessToken', user?.accessToken);
    localStorage.setItem('refreshToken', user?.refreshToken);

    if (user?.accessTokenExpiresAt) {
      localStorage.setItem('accessTokenExpiresAt', user.accessTokenExpiresAt);
    }
    if (user?.refreshTokenExpiresAt) {
      localStorage.setItem('refreshTokenExpiresAt', user.refreshTokenExpiresAt);
    }

    if (employeeConfiguration && employeeConfiguration.length > 0) {
      employeeConfiguration.forEach((cfg) => {
        if (!cfg?.property) return;
        const prop = cfg.property;
        const value = cfg.propertyValue;
        if (prop === 'PreferredTheme' && value) {
          localStorage.setItem('PreferredTheme', value);
          setCurrentColor(value);
        } else if (prop === 'PreferredLanguage' && value) {
          const v = String(value).toLowerCase();
          const lang = v.startsWith('ar') ? 'ar' : 'en';
          localStorage.setItem('PreferredLanguage', lang);
          setCurrentLanguage(lang);
        } else if (prop === 'PreferredPaginationRows' && value) {
          localStorage.setItem('PreferredPaginationRows', String(value));
        } else if (prop === 'PreferredLeftMenue' && value) {
          localStorage.setItem('PreferredLeftMenue', String(value));
        }
      });
    }

    return 'DataCreated';
  } catch (error) {
    console.error('Error in saveTokensToLocalStorage:', error);
  }
};

export const saveUserPrivilegestoLocalStorage = async (privileges) => {
  if (privileges?.length > 0) {
    if (!localStorage.getItem('userPrivileges')) {
      var objprivileges = privileges?.map((item) => {
        return {
          privilegeID: item.id,
          isAdd: item.isAdd,
          isEdit: item.isEdit,
          isDelete: item.isDelete,
          isCopy: item.isCopy,
          isExport: item.isExport,
          isView: item.isView,
          menuItemID: item.menuItemID,
          userTypeID: item.userTypeID,
          menuItemCode: item?.menuItem?.code,
        };
      });
      localStorage.setItem('userPrivileges', JSON.stringify(objprivileges));
      return 'DataInserted';
    }
  }
};

export const getUserPrivileges = () => {
  const privileges = localStorage.getItem('userPrivileges');
  return privileges ? JSON.parse(privileges) : [];
};

export const getAccessToken = () => {
  return localStorage.getItem('accessToken');
};

export const getRefreshToken = () => {
  return localStorage.getItem('refreshToken');
};

export const saveRefreshTokensToLocalStorage = (payload, fallbackRefreshToken) => {
  try {
    if (!payload || typeof payload !== 'object') return;
    const accessToken = payload?.accessToken;
    if (!accessToken) return;

    const refreshToken = payload?.refreshToken || fallbackRefreshToken;
    const accessTokenExpiresAt = payload?.accessTokenExpiresAt;
    const refreshTokenExpiresAt = payload?.refreshTokenExpiresAt;

    localStorage.setItem('accessToken', accessToken);

    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }

    if (accessTokenExpiresAt) {
      localStorage.setItem('accessTokenExpiresAt', accessTokenExpiresAt);
    }
    if (refreshTokenExpiresAt) {
      localStorage.setItem('refreshTokenExpiresAt', refreshTokenExpiresAt);
    }
  } catch (e) {
    console.error('Error in saveRefreshTokensToLocalStorage:', e);
  }
};

const resolveRefreshEmployeeInfo = (payload) => {
  if (!payload) return null;
  if (payload?.employeeInfo && typeof payload.employeeInfo === 'object') return payload.employeeInfo;
  if (payload?.data?.employeeInfo && typeof payload.data.employeeInfo === 'object') return payload.data.employeeInfo;
  if (payload?.data?.data?.employeeInfo && typeof payload.data.data.employeeInfo === 'object') return payload.data.data.employeeInfo;
  return null;
};

const extractEmployeeRoleFlagsPatch = (employeeInfo) => {
  if (!employeeInfo || typeof employeeInfo !== 'object') return {};

  const patch = {};
  if (employeeInfo.isManager !== undefined) patch.isManager = employeeInfo.isManager;
  if (employeeInfo.IsManager !== undefined) patch.isManager = employeeInfo.IsManager;

  if (employeeInfo.normalUser !== undefined) patch.normalUser = employeeInfo.normalUser;
  if (employeeInfo.isNormalEmployee !== undefined) patch.normalUser = employeeInfo.isNormalEmployee;
  if (employeeInfo.IsNormalEmployee !== undefined) patch.normalUser = employeeInfo.IsNormalEmployee;

  if (employeeInfo.isMultiRole !== undefined) patch.isMultiRole = employeeInfo.isMultiRole;
  if (employeeInfo.IsMultiRole !== undefined) patch.isMultiRole = employeeInfo.IsMultiRole;

  if (employeeInfo.isHr !== undefined) patch.isHr = employeeInfo.isHr;
  if (employeeInfo.IsHr !== undefined) patch.isHr = employeeInfo.IsHr;

  if (employeeInfo.isSecurity !== undefined) patch.isSecurity = employeeInfo.isSecurity;
  if (employeeInfo.IsSecurity !== undefined) patch.isSecurity = employeeInfo.IsSecurity;

  if (employeeInfo.isEntityPrivilege !== undefined) patch.isEntityPrivilege = employeeInfo.isEntityPrivilege;
  if (employeeInfo.IsEntityPrivilege !== undefined) patch.isEntityPrivilege = employeeInfo.IsEntityPrivilege;

  if (employeeInfo.roleID !== undefined) patch.roleID = employeeInfo.roleID;
  if (employeeInfo.roleId !== undefined) patch.roleID = employeeInfo.roleId;
  if (employeeInfo.RoleID !== undefined) patch.roleID = employeeInfo.RoleID;

  return patch;
};

export const patchEmployeeInfoRoleFlagsFromRefresh = (refreshPayload) => {
  try {
    const incomingEmployeeInfo = resolveRefreshEmployeeInfo(refreshPayload);
    if (!incomingEmployeeInfo) return null;

    const patch = extractEmployeeRoleFlagsPatch(incomingEmployeeInfo);
    if (!patch || Object.keys(patch).length === 0) return null;

    let existingEmployeeInfo = {};
    try {
      const raw = localStorage.getItem('employeeInfo');
      existingEmployeeInfo = raw ? JSON.parse(raw) : {};
    } catch (e) {
      existingEmployeeInfo = {};
    }

    const nextEmployeeInfo = {
      ...(existingEmployeeInfo || {}),
      ...patch,
    };

    localStorage.setItem('employeeInfo', JSON.stringify(nextEmployeeInfo));
    patchCurrentUserToLocalStorage(patch);

    return patch;
  } catch (e) {
    console.error('Error in patchEmployeeInfoRoleFlagsFromRefresh:', e);
    return null;
  }
};

export const getAssignRoleKey = () => {
  const assignRoleKey = localStorage.getItem('assignRoleKey');
  if (assignRoleKey && assignRoleKey !== 'null' && assignRoleKey !== 'undefined') {
    return assignRoleKey;
  }

  return null;
};

export const getAssignRoleID = () => {
  return getAssignRoleKey();
};

export const setCurrentUser = (user) => {
  try {
    if (user) {
      localStorage.setItem('currentUser', JSON.stringify(user));
    } else {
      localStorage.removeItem('currentUser');
    }
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : setCurrentUser -> error', error);
  }
};

export const mapingApiList = (data, mapperFunction) => {
  return data?.map(mapperFunction);
};

export const isHrEmployee = () => {
  const currentUser = getCurrentUser();
  if (currentUser?.isHr) return true;
  return false;
};

export const isSecurityEmployee = () => {
  const currentUser = getCurrentUser();
  if (currentUser?.isSecurity) return true;
  return false;
};

export const isManager = () => {
  const currentUser = getCurrentUser();
  if (currentUser?.isManager) return true;
  return false;
}

export const isEntityPrivilege = () => {
  const currentUser = getCurrentUser();
  if (currentUser?.isEntityPrivilege) return true;
  return false;
}

export const getEmployeeLang = () => {
  const currentUser = getCurrentUser();
  if (currentUser.lang === 2) return 'ar';
  else return 'en';
};

export const getCurrentEmployeeGroupID = () => {
  const currentUser = getCurrentUser();
  return currentUser.groupIds;
};

export const checkIfEmployeeInGroup = (groupID) => {
  const currentUser = getCurrentUser();

  if (currentUser.groupIds) {
    if (currentUser.groupIds.find((x) => x === groupID)) return true;
  }
  return false;
};

export const validateInputField = ({
  fieldType,
  value,
  regularExpresion,
  minLength,
  maxLength,
  minYear,
  matchValue,
}) => {
  let error;
  if (fieldType === 'NUMBER') {
    return isNumeric(value);
  } else if (fieldType === 'TEXT' || !fieldType) {
    if (value === '') {
      error = 'Mandatory';
    } else if (matchValue && value !== matchValue) error = 'Erorr';
    else if (!value?.match(regularExpresion) && regularExpresion !== undefined)
      error = 'Erorr';
    else if (value?.length < minLength && minLength)
      error = `min length is${minLength}`;
    else if (value?.length > maxLength && maxLength)
      error = `max length is${maxLength}`;
    return error;
  } else if (fieldType === 'DROPDOWN') {
    if (value < 0) return 'Mandatory';
  } else if (fieldType === 'DATE') {
    const differenceInYear =
      new Date().getFullYear() - new Date(value).getFullYear();
    if (differenceInYear < minYear && minYear) return `max year is${minYear}`;
  } else if (fieldType === 'EMAIL') {
    return validateEmail(value);
  }
  return error;
};

export const validateEmail = (value) => {
  let error;
  if (!value) {
    error = 'Please enter your email address';
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
    error = 'Invalid email address';
  }
  return error;
};

const isNumeric = (str) => {
  if (!/^[0-9]+$/.test(str)) return 'Wrong Format';
};

export const getBase64 = (file, cb) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = function () {
    cb(reader.result);
  };
  reader.onerror = function (error) {
    console.log('Error: ', error);
  };
};

export const checkDevelopmentCurrentStatus = () => {
  let currentStatusApplication = 'Development';
  if (currentStatusApplication === 'Development') return true;
  else return false;
}

function customEqualityCheck(prev, next) {
  // If both are the same reference, they are equal
  if (prev === next) {
    return true;
  }

  if (prev == null || next == null) {
    return prev === next;
  }

  if (typeof prev !== 'object' || typeof next !== 'object') {
    return prev === next;
  }

  // You can use deep comparison logic here if needed

  // Example: Shallow comparison of objects
  const prevKeys = Object.keys(prev);

  const nextKeys = Object.keys(next);

  // If number of keys is different, objects are not equal
  if (prevKeys.length !== nextKeys.length) {
    return false;
  }

  // Check if all keys and their values are the same
  for (let key of prevKeys) {
    if (prev[key] !== next[key]) {
      return false;
    }
  }

  return true;
}

export function useMemoizedSelector(selector) {
  // useSelector(selector, (prev, next) => prev === next);

  return useSelector(selector, customEqualityCheck);
}

export function isValidPropsDetails(details) {
  const { errors } = details;
  for (let key in errors) {
    if (errors.hasOwnProperty(key) && errors[key] !== '') {
      return false;
    }
  }
  return true;
}

export function GetPolicyAddonValues(stateObject) {
  const listAddOnValue = [];
  Object.keys(stateObject).forEach((key) => {
    if (stateObject[key].value !== null && key !== 'code') {
      listAddOnValue.push({
        code: key,
        value: stateObject[key],
      });
    }
  });

  return listAddOnValue;
}

// export function getCompanyID() {
//   return '7D85FE1D-00C3-420B-B0F3-97BD7A4DDCE5';
// }
// export function getEmployeeID() {
//   return 'EB8B61CF-9387-4F00-98CF-08DC418DCED0';
// }

// export const getCompanyID = () => {
//   const userData = JSON.parse(localStorage.getItem('current_user'));
//   if (userData) {
//     return userData.companyID;
//   } else {
//     return null; // Return null if user data is not found in sessionStorage
//   }
// };

// src/helpers/Utils.js (if it doesn't exist)
// export const getCompanyID = () => {
//   // Your logic to get company ID from localStorage, context, or wherever it's stored
//   return localStorage.getItem('companyID') || '402AA1DA-B7CB-40E4-9A24-BAB749B369A4'; // fallback
// };

export const getCompanyID = () => {
  try {
    const assignedCompanyId = localStorage.getItem('assignRoleCompanyID');
    if (assignedCompanyId && assignedCompanyId !== 'null' && assignedCompanyId !== 'undefined') {
      return assignedCompanyId;
    }
  } catch (e) {
  }

  return getBaseCompanyID();
}

export const getBaseCompanyID = () => {
  try {
    const raw = localStorage.getItem('currentUser');
    const userData = raw ? JSON.parse(raw) : null;
    if (userData && userData.companyID) {
      return userData.companyID;
    }
  } catch (e) {
  }

  try {
    return localStorage.getItem('companyID');
  } catch (e) {
    return null;
  }
};

export const getUserRolePrivileges = () => {
  const currentAssignRoleKey = getAssignRoleKey();
  if (currentAssignRoleKey) {
    return currentAssignRoleKey;
  }

  return localStorage.getItem('userRolePrivileges') || null;
};

export const isItemPinned = (itemId) => {
  // Your pin manager logic
  const pinnedItems = JSON.parse(localStorage.getItem('pinnedItems') || '[]');
  return pinnedItems.includes(itemId);
};

export const getEmployeeID = () => {
  const userData = getCurrentUser();
  if (userData) {
    return userData.id;
  } else {
    return null; // Return null if user data is not found in sessionStorage
  }
};

export const UpdateLists = (
  list,
  filteredList,
  updatedItem,
  actionType,
  keyWord,
  filteration = (item) =>
    item.nameAr.toLowerCase().includes(keyWord.toLowerCase()) ||
    item.nameEn.toLowerCase().includes(keyWord.toLowerCase())
) => {
  let updatedList, updatedFilteredList;

  switch (actionType) {
    case 'insert':
      updatedList = [...list, updatedItem];
      updatedFilteredList = [...filteredList, updatedItem];
      break;
    case 'update':
      updatedList = list.map((item) =>
        item.id === updatedItem.id ? updatedItem : item
      );
      updatedFilteredList = filteredList.map((item) =>
        item.id === updatedItem.id ? updatedItem : item
      );
      break;
    case 'delete':
      updatedList = list.filter((item) => item.id !== updatedItem.id);
      updatedFilteredList = filteredList.filter(
        (item) => item.id !== updatedItem.id
      );
      break;
    case 'filter':
      updatedList = list;
      updatedFilteredList = keyWord === '' ? list : list.filter(filteration);
      break;
    default:
      console.warn(`Unknown actionType: ${actionType}`);
      return { list, filteredList };
  }

  return { list: updatedList, filteredList: updatedFilteredList };
};

// export const showNotificationMiddlware = (
//   errorCode,
//   showNotification,
//   customMessage
// ) => {
//   // let message = '';
//   // let color = '';
//   // let code = '';
//   // const intl = useIntl();

//   // switch (errorCode) {
//   //   case '200':
//   //     code = 'notification.success';
//   //     color = 'success';
//   //     break;
//   //   case '201':
//   //     code = 'notification.created';
//   //     color = 'success';
//   //     break;
//   //   case '400':
//   //     code = 'notification.badRequest';

//   //     color = 'danger';
//   //     break;
//   //   case '401':
//   //     code = 'notification.unauthorized';
//   //     color = 'danger';
//   //     break;
//   //   case '500':
//   //     code = 'notification.internalServerError';
//   //     color = 'danger';
//   //     break;
//   //   case '600':
//   //     code = 'notification.requiredFields';
//   //     //message = 'Please Fill All Required Fields';
//   //     color = 'warning';
//   //     break;
//   //   case '-1':
//   //     code = 'notification.approvalRequiredFields';
//   //     //  message = 'Please Add Approval Levels & Fill All Required Fields';
//   //     color = 'warning';
//   //     break;
//   //   case '-99':
//   //     code = 'notification.leaveBalanceCheck';
//   //     //message = 'Requested Leaves are greater then Balance';
//   //     color = 'danger';
//   //     break;
//   //   case 'PermissionValidData':
//   //     code = 'notification.permissionValidData';
//   //     //message = 'Please enter valid inputs';
//   //     color = 'warning';
//   //     break;
//   //   case 'ApprovalExistsRequest':
//   //     code = 'notification.approvalExistsRequest';
//   //     // message =
//   //     //   'The request cannot be deleted because its status has already been updated.';
//   //     color = 'warning';
//   //     break;
//   //   case 'PermissionSameYear':
//   //     code = 'notification.permissionSameYear';
//   //     // message = 'fromDate and toDate are not in the same year';
//   //     color = 'warning';
//   //     break;
//   //   case 'LeaveValidData':
//   //     code = 'notification.leaveValidData';
//   //     //message = 'Please enter valid inputs';
//   //     color = 'warning';
//   //     break;
//   //   case 'emailValidation':
//   //     code = 'notification.emailValidation';
//   //     //message = 'Please enter valid email';
//   //     color = 'warning';
//   //     break;
//   //   case 'EmployeeNoCardCheck':
//   //     code = 'notification.employeeNoCardCheck';
//   //     // message =
//   //     //   'Either Employee Number or Card Number already used. Please ensure both are unique before proceeding.';
//   //     color = 'warning';
//   //     break;
//   //   case 'ExportFinished':
//   //     code = 'notification.exportFinished';
//   //     // message =
//   //     //   'Exported successfully, Please check the download folder to find the exported list.';
//   //     color = 'success';
//   //     break;
//   //   default:
//   //     message = customMessage;
//   //     color = 'warning';
//   //     break;
//   // }
//   // ;

//   showNotification(message, color, code);
// };

export const isValidYear = (year) => {
  return moment(year, 'YYYY', true).isValid();
};

export const pushToFront = (prevArray, newElement) => {
  return (prevArray) => [newElement, ...prevArray];
};

// public const string Ok = "200";
// public const string Created = "201";

// // 4xx Client Errors
// public const string BadRequest = "400";
// public const string Unauthorized = "401";
// public const string Forbidden = "403";
// public const string NotFound = "404";

// // 5xx Server Errors
// public const string InternalServerError = "500";
// public const string BadGateway = "502";
// public const string ServiceUnavailable = "503";
export const getDaysOfWeek = (data, date) => {
  const daysOfWeek = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
  let days = [];
  if (!date) {
    for (let i = 0; i < 7; i++) {
      const dayIndex = i % 7;
      days.push({
        weekIndex: 0,
        id: i + 1,
        selectedDayIndex: i + 1,
        name: daysOfWeek[dayIndex],
        date: new Date(),
        value: data?.find((x) => x.selectedDayIndex === i + 1) ? true : false,
      });
    }
  } else {
    const month = new Date(date).getMonth(); // Get current month (zero-based index)
    const year = new Date(date).getFullYear();
    const firstDayOfMonth = new Date(year, month, 1); // First day of the current month
    const currentDay = firstDayOfMonth.getDay(); // Get the day of the week for the first day (0 = Sunday, 1 = Monday, ...)
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate(); // Get the last day of the month

    // Add blank days if the month doesn't start on Saturday
    const offsetToSaturday = (currentDay + 1) % 7; // This will ensure the array starts from Saturday

    // Add blank days until the first Saturday
    for (let i = 0; i < offsetToSaturday; i++) {
      days.push({
        weekIndex: 0,
        id: i,
        selectedDayIndex: 0,
        name: daysOfWeek[i],
        date: null,
        value: false,
      });
    }

    // Loop through the days of the month and add them to the array
    for (let i = 0; i < lastDayOfMonth; i++) {
      const dayIndex = (offsetToSaturday + i) % 7;
      const currentDate = new Date(year, month, i + 1); // Current day in the month
      days.push({
        weekIndex: Math.floor((i + offsetToSaturday) / 7),
        id: i + 1,
        name: daysOfWeek[dayIndex],
        selectedDayIndex: i + 1,
        date: currentDate,
        value: data?.find((x) => x.selectedDayIndex === i + 1) ? true : false,
      });
    }
  }
  return days;
};
// export const generateDateTimeList = (
//   min = '06:00:00',
//   max = '18:00:00',
//   step = 15
// ) => {
//   const dateTimeList = [];
//   let minDateTime = moment(new Date(min)),
//     maxDateTime = moment(new Date(max));
//   console.log(minDateTime);
//   console.log(maxDateTime);
//   while (minDateTime <= maxDateTime) {
//     dateTimeList.push({
//       value: new Date(minDateTime),
//       label: new Date(minDateTime),
//     });
//     minDateTime.setMinutes(minDateTime.getMinutes() + step);
//   }
//   return dateTimeList;
// };

export function getArrayValues(max = 240) {
  const values = [];
  for (let i = 1; i <= max; i++) {
    values.push({ id: i, value: i.toString(), label: i });
  }
  return values;
}

export function getArrayTime(
  fromTime = '00:15',
  toTime = '23:45',
  step = 15,
  inputTimeFrom,
  inputTimeTo
) {
  const options = [];
  try {
    // if (inputTimeFrom) {
    //   options.push({
    //     id: inputTimeFrom,
    //     label: inputTimeFrom.split(":").slice(0, 2).join(":"),
    //     index: options.length,
    //     minutes: timeToMinutes(inputTimeFrom),
    //   });
    // }
    // if (inputTimeTo) {
    //   options.push({
    //     id: inputTimeTo,
    //     label: inputTimeTo.split(":").slice(0, 2).join(":"),
    //     index: options.length,
    //     minutes: timeToMinutes(inputTimeTo),
    //   });
    // }
    let index = options.length;
    for (
      let timeInMinutes = convertTimeToMinutes(fromTime);
      timeInMinutes <= convertTimeToMinutes(toTime);
      timeInMinutes += step
    ) {
      const hours = Math.floor(timeInMinutes / 60);
      const minutes = timeInMinutes % 60;
      const time = `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}`;
      options.push({
        id: time,
        label: time,
        index: index,
        minutes: timeInMinutes,
        value: timeInMinutes,
      });
      index++;
    }
    return options.sort((a, b) => a.minutes - b.minutes);
  } catch {
    return [];
  }
}
 
export function convertTimeToMinutes(timeString) {
  const momentTime = moment(timeString, 'HH:mm');

  const totalMinutes = momentTime.hours() * 60 + momentTime.minutes();
  return totalMinutes;
}
export function convertTimeHHMMToMinutes(timeString) {
  try {
    const totalMinutes =
      parseInt(timeString.split(':')[0]) * 60 +
      parseInt(timeString.split(':')[1]);
    return totalMinutes;
  } catch (error) {
    console.log(error);
    return 0;
  }
}
export function convertHoursToMinutes(hours) {
  try {
    const totalMinutes = hours * 60;
    return totalMinutes;
  } catch (error) {
    console.log(error);
    return 0;
  }
}
export function convertMinutesToHours(minutes) {
  try {
    const totalHours = parseFloat((minutes / 60).toFixed(2));
    return totalHours;
  } catch (error) {
    console.log(error);
    return 0;
  }
}
export const timeHourOptionsFunc = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    times.push({ value: hour, label: hour.toString().padStart(2, '0') });
  }
  return times;
};
export const timeMinuteOptionsFunc = () => {
  const times = [];
  for (let minute = 0; minute < 60; minute += 1) {
    times.push({
      value: minute,
      label: minute.toString().padStart(2, '0'),
    });
  }
  return times;
};
export function convertMinutesToHHMM(minutes) {
  // const momentTime = moment().startOf('day').minutes(minutes);
  // return momentTime.format('HH:mm');

  const momentTime = moment().startOf('day').add(minutes, 'minutes'); // Properly add minutes
  return momentTime.format('HH:mm'); // Format to HH:mm
}

export function isValidEmail(email) {
  try {
    if (!email || email.length === 0 || email.charAt(0) == '.') return false;
    else return /^[\w\.-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z]+)+$/.test(email);
  } catch {
    return false;
  }
}
export function getItemsByCodes(listAddon, code) {
  const addonItem = listAddon?.find((item) => item.code === code);
  if (addonItem) {
    return addonItem.value;
  }

  return 0;
}

function getCurrentTimeInMinutes() {
  const currentTime = new Date();
  return currentTime.getHours() * 60 + currentTime.getMinutes();
}
function validateInputs(...inputs) {
  inputs.forEach((input) => {
    if (typeof input !== 'number' || input < 0) {
      throw new Error('Inputs must be non-negative numbers.');
    }
  });
}

export function convertBase64(file) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      resolve(fileReader.result);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
}
export function mergeAddons(listAddon, policyAddOnsValue) {
  // Create a map for easy reference lookup
  const referenceMap = {};
  policyAddOnsValue.forEach((item) => {
    referenceMap[item.reference] = item.value;
  });

  // Iterate over listAddon and update values if reference exists
  listAddon.forEach((addon) => {
    Object.keys(addon).forEach((key) => {
      if (referenceMap.hasOwnProperty(key)) {
        addon[key] = referenceMap[key];
      }
    });
  });
  return listAddon;
}

export const getDayNameByIndex = (dayIndex) => {
  return Object.keys(WeekDays).find(
    (dayName) => WeekDays[dayName] === dayIndex
  );
};

export const getYears = () => {
  const yearsArray = [];
  const start = new Date().getFullYear();
  for (let year = start; year <= start + 50; year++) {
    yearsArray.push({ value: year.toString(), label: year.toString() });
  }
  return yearsArray;
};
export function calculateOverallProgress(
  intTime,
  minFromTime,
  schIn,
  schOutMax,
  expectedOut,
  isToday,
  actualOut = expectedOut
) {
  const totalDuration = schOutMax - schIn;
  // If total duration is invalid, return all zeros
  if (totalDuration <= 0 || intTime === 0) {
    return {
      preProgress: 0,
      delayPercentage: 0,
      sufProgress: 0,
      comingPercentage: 0,
      workPerc: 0, // Added work percentage
      actualWorkDuration: 0,
      actualWorkDuration: 0,
      delayPercentageValue: 0,
      sufProgressValue: 0,
      comingPercentageValue: 0,
      fullProgressPercentage: 100,
    };
  }

  const currentTimeInMinutes = isToday ? getCurrentTimeInMinutes() : actualOut;
  let preProgress = 0;
  let delayPercentage = 0;
  let sufProgress = 0;
  let comingPercentage = 0;
  let workPerc = 0;
  let actualWorkDuration = 0;
  // 1. Calculate Pre-Progress
  // if (intTime >= minFromTime) {
  //   const duration = Math.min(schIn, intTime) - minFromTime;
  //   preProgress = (duration / totalDuration) * 100;
  // }

  // 2. Calculate Delay Percentage
  if (intTime > schIn) {
    const delay = intTime - schIn;
    delayPercentage = (delay / totalDuration) * 100;
  }

  // 3. Calculate Suffix Progress
  // if (expectedOut < schOutMax) {
  //   const remainingTime = schOutMax - expectedOut;
  //   sufProgress = (remainingTime / totalDuration) * 100;
  // }

  // 4. Calculate Coming Percentage
  if (currentTimeInMinutes < expectedOut && isToday) {
    const comingBalance = expectedOut - currentTimeInMinutes;
    comingPercentage = (comingBalance / totalDuration) * 100;
  }

  // 5. Calculate Work Percentage
  if (currentTimeInMinutes >= intTime && currentTimeInMinutes <= expectedOut) {
    const elapsedWorkTime = currentTimeInMinutes - intTime;
    //const totalWorkTime = expectedOut - schIn;
    actualWorkDuration = elapsedWorkTime;
    workPerc = (elapsedWorkTime / totalDuration) * 100;
  } else if (expectedOut > schOutMax) {
    const elapsedWorkTime = expectedOut - intTime;
    //const totalWorkTime = expectedOut - schIn;
    actualWorkDuration = elapsedWorkTime;

    workPerc = (elapsedWorkTime / totalDuration) * 100;
  }
  // Adjust percentages to ensure the total equals 100%
  const totalProgress =
    preProgress + delayPercentage + sufProgress + comingPercentage;

  // You can now return both the percentage and the value
  const preProgressValue = convertMinutesToHHMM(
    (preProgress / 100) * totalDuration
  );
  const delayPercentageValue = convertMinutesToHHMM(
    (delayPercentage / 100) * totalDuration
  );
  const sufProgressValue = convertMinutesToHHMM(
    (sufProgress / 100) * totalDuration
  );
  const comingPercentageValue = convertMinutesToHHMM(
    (comingPercentage / 100) * totalDuration
  );

  if (totalProgress > 100) {
    const adjustmentFactor = 100 / totalProgress;
    preProgress *= adjustmentFactor;
    delayPercentage *= adjustmentFactor;
    sufProgress *= adjustmentFactor;
    comingPercentage *= adjustmentFactor;
  }

  console.log({
    totalDuration: totalDuration,
    preProgress: Math.max(0, Math.min(preProgress, 100)),
    delayPercentage: Math.max(0, Math.min(delayPercentage, 100)),
    sufProgress: Math.max(0, Math.min(sufProgress, 100)),
    comingPercentage: Math.max(0, Math.min(comingPercentage, 100)),
    workPerc: Math.max(0, Math.min(workPerc, 100)), // Ensure work percentage is valid
    actualWorkDuration: convertMinutesToHHMM(actualWorkDuration),
    // You can now return both the percentage and the value
    preProgressValue: preProgressValue,
    delayPercentageValue: delayPercentageValue,
    sufProgressValue: sufProgressValue,
    comingPercentageValue: comingPercentageValue,
  });

  return {
    preProgress: Math.max(0, Math.min(preProgress, 100)),
    delayPercentage: Math.max(0, Math.min(delayPercentage, 100)),
    sufProgress: Math.max(0, Math.min(sufProgress, 100)),
    comingPercentage: Math.max(0, Math.min(comingPercentage, 100)),
    workPerc: Math.max(0, Math.min(workPerc, 100)), // Ensure work percentage is valid
    actualWorkDuration: convertMinutesToHHMM(actualWorkDuration),
    // You can now return both the percentage and the value
    preProgressValue: preProgressValue,
    delayPercentageValue: delayPercentageValue,
    sufProgressValue: sufProgressValue,
    comingPercentageValue: comingPercentageValue,
  };
}

// export function calculatePreProgressPercentage(
//   intTime,
//   minFromTime,
//   schIn,
//   schOut
// ) {
//   validateInputs(intTime, minFromTime, schIn, schOut);

//   if (schOut <= schIn) {
//     return 0;
//   }

//   if (intTime >= minFromTime) {
//     const duration = Math.min(schIn, intTime) - minFromTime;
//     return Math.min((duration / (schOut - schIn)) * 100, 100); // Clamp to 100%
//   }
//   return 0;
// }

// export function calculateSufProgressPercentage(expectedOut, totalDuration) {
//   validateInputs(expectedOut, totalDuration);

//   if (totalDuration <= 0) {
//     throw n0;
//   }

//   const currentTimeInMinutes = getCurrentTimeInMinutes();
//   if (expectedOut < currentTimeInMinutes) return 0;

//   const remainingPercentage =
//     ((expectedOut - currentTimeInMinutes) / totalDuration) * 100;

//   return Math.min(remainingPercentage, 100); // Clamp to 100%
// }

// export function calculateDelayPercentage(intTime, schIn, totalDuration) {
//   validateInputs(intTime, schIn, totalDuration);

//   if (totalDuration <= 0) {
//     return 0;
//   }

//   if (intTime < schIn) return 0;

//   const delay = intTime - schIn;
//   return Math.min((delay / totalDuration) * 100, 100); // Clamp to 100%
// }

// export function calculateComingPercentage(expectedOut, totalDuration) {
//   validateInputs(expectedOut, totalDuration);

//   if (totalDuration <= 0) {
//     return 0;
//   }

//   const currentTimeInMinutes = getCurrentTimeInMinutes();
//   if (expectedOut > currentTimeInMinutes) return 0;

//   const comingBalance = expectedOut - currentTimeInMinutes;
//   return Math.max((comingBalance / totalDuration) * 100, 0); // Ensure no negative percentages
// }

// export function calculatePresentPercentage(inTime, expectedOut, totalDuration) {
//   validateInputs(inTime, expectedOut, totalDuration);

//   if (totalDuration <= 0) {
//     return 0;
//   }

//   if (inTime > 0 && expectedOut > 0) {
//     const currentTimeInMinutes = getCurrentTimeInMinutes();

//     // Ensure elapsed time doesn't exceed total duration
//     const elapsedTime = Math.min(currentTimeInMinutes, expectedOut) - inTime;

//     return Math.min((elapsedTime / totalDuration) * 100, 100); // Clamp to 100%
//   }
//   return 0;
// }
// export function calculatePresent(inTime, expectedOut) {
//   if (inTime > 0 && expectedOut > 0) {
//     // Get the current time
//     const currentTime = new Date();
//     const currentHour = currentTime.getHours();
//     const currentMinute = currentTime.getMinutes();
//     const currentTimeInMinutes = currentHour * 60 + currentMinute;

//     // Calculate total duration
//     // /  const totalDuration = expectedOut - inTime;

//     // Calculate elapsed time
//     return currentTimeInMinutes - inTime;

//     // // Calculate percentage completion
//     // const percentage = (elapsedTime / totalDuration) * 100;

//     // return Math.min(percentage, 100); // Ensure percentage does not exceed 100
//   }
//   return 0;
// }

export function getOptionValue(workFlowValuesList, stageID, optionID) {
  let form = workFlowValuesList?.find((x) => x.stageID === stageID);
  if (form) {
    return form?.list?.find((x) => x.formOptionID === optionID)
      ?.formOptionValue;
  }
  return '';
}

export function getFormActionValue(
  workFlowValuesList,
  workFlowList,
  formID,
  stageOrder
) {
  const currentWorkFlowValue = workFlowValuesList.find(
    (x) => x.formID === formID && x.stageOrder === stageOrder
  );

  if (currentWorkFlowValue) {
    return currentWorkFlowValue.action;
  } else {
    const currentWorkFlow = workFlowList.find(
      (x) => x.formID === formID && x.stageOrder === stageOrder
    );

    if (currentWorkFlow) {
      // Find the last workflow stage value
      const lastWorkFlowStageValue = workFlowValuesList?.reduce((max, item) => {
        return max.stageOrder > item.stageOrder ? max : item;
      });
      // Find the last workflow stage
      const lastWorkFlowStage = workFlowList?.find(
        (x) => x.stageOrder > lastWorkFlowStageValue.stageOrder
      );

      if (
        lastWorkFlowStageValue.stageOrder + 1 === currentWorkFlow.stageOrder &&
        lastWorkFlowStageValue.action !== 3
      )
        return 0;
      if (lastWorkFlowStage) {
        // Compare current and last workflow stages
        if (
          lastWorkFlowStage.stageOrder === currentWorkFlow.stageOrder ||
          (lastWorkFlowStage.stageOrder + 1 === currentWorkFlow.stageOrder &&
            lastWorkFlowStageValue.action !== 3)
        ) {
          return 0; // Return 0 if current stage is the last stage
        } else {
          return -99; // Return -99 if not the last stage
        }
      }
    }
  }

  // Return -1 if no action found
  return -1;
}

export const getDiffDate = (iFromDate, iToDate) => {
  try {
    // const fromDate = moment(iFromDate, generalFormatDate);
    // const toDate = moment(iToDate, generalFormatDate);

    // return toDate.diff(fromDate, 'days') >= 0
    //   ? toDate.diff(fromDate, 'days') + 1
    //   : toDate.diff(fromDate, 'days');
    const diff = getDateDiff(iFromDate,iToDate);
    return diff >= 0 ? diff + 1 : diff;
  } catch (e) {
    console.log(e);
    return 0;
  }
};

export const getDateDiff = (fromDateStr, toDateStr) => {
  const primaryFormat = generalFormatDateForTableDate;
  const fallbackFormat = generalFormatDate;

  // Parse both dates with strict checking
  let fromDate = moment(fromDateStr, primaryFormat, true);
  let toDate = moment(toDateStr, primaryFormat, true);

  // Fallback parsing if strict fails
  if (!fromDate.isValid()) {
    fromDate = moment(fromDateStr, fallbackFormat);
  }

  if (!toDate.isValid()) {
    toDate = moment(toDateStr, fallbackFormat);
  }

  // If either date is still invalid, return 0
  if (!fromDate.isValid() || !toDate.isValid()) {
    console.warn('Invalid date(s) passed:', { fromDateStr, toDateStr });
    return -1;
  }
  fromDate = fromDate?.startOf('day');
  toDate = toDate?.startOf('day');

  // Calculate the difference (inclusive)
  const diff = toDate?.diff(fromDate, 'days');
  return diff;
}

export const checkIfEqualDate = (iFromDate, iToDate) => {
  try {
    const fromDate = moment(iFromDate, generalFormatDate, true);
    const toDate = moment(iToDate, generalFormatDate, true);
    if (!fromDate.isValid() || !toDate.isValid()) {
      console.error('Invalid date format');
      return false;
    }
    return toDate.startOf('day').diff(fromDate.startOf('day'), 'days') === 0;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export function getDeviceType() {
  const userAgent = navigator.userAgent;
  if (/android/i.test(userAgent)) {
    return 'ANDROID';
  } else if (/iPad|iPhone|iPod/i.test(userAgent)) {
    return 'IOS';
  } else {
    return '';
  }
}

export function getdateDifference(fromdate, todate) {
  const startDate = new Date(fromdate);
  const endDate = new Date(todate);

  let years = endDate.getFullYear() - startDate.getFullYear();
  let months = endDate.getMonth() - startDate.getMonth();
  let days = endDate.getDate() - startDate.getDate();

  if (days < 0) {
    months -= 1;
    days += new Date(endDate.getFullYear(), endDate.getMonth(), 0).getDate();
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return `${years} years, ${months} months, ${days} days`;
}

export function getTimeDiff(fromTime, toTime) {
  const [hoursA, minutesA] = fromTime.split(':').map(Number);
  const [hoursB, minutesB] = toTime.split(':').map(Number);

  const totalMinutesA = hoursA * 60 + minutesA;
  const totalMinutesB = hoursB * 60 + minutesB;

  let diffMinutes = Math.abs(totalMinutesA - totalMinutesB);

  const diffHours = Math.floor(diffMinutes / 60);
  diffMinutes = diffMinutes % 60;

  return `${diffHours} Hours ${diffMinutes} Mins`;
}

export function getDateTime(dateTime) {
  const date = new Date(dateTime);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

export const formatEmiratesId = (value) => {
  value = value?.replace(/\D/g, ''); // Remove non-numeric characters

  if (value?.length > 3) {
    value = value?.substring(0, 3) + '-' + value?.substring(3);
  }
  if (value?.length > 8) {
    value = value?.substring(0, 8) + '-' + value?.substring(8);
  }
  if (value?.length > 16) {
    value = value?.substring(0, 16) + '-' + value?.substring(16);
  }
  const formattedValue = value?.substring(0, 18); // Max length of 18 (including hyphens)
  return formattedValue;
};

export const convertHoursMinutes = (value) => {
  value = value?.replace(/\D/g, ''); // Remove non-numeric characters
  let hours = value?.substr(0, 2);
  let minute = value?.substr(2, 4);

  if (hours >= 24) {
    hours = '23';
  }

  if (hours === '') {
    hours = '00';
  } else {
    if (hours < 10) {
      hours = '0' + parseInt(hours);
    }
  }

  if (minute >= 60) {
    minute = 59;
  }

  if (minute === '') {
    minute = '00';
  } else {
    if (minute < 10) {
      minute = '0' + parseInt(minute);
    }
  }
  return hours + ':' + minute;
};

export const handleSorting = (valA, valB, columnId, desc) => {
  // Get the value of the column to sort by
  const a = valA[columnId];
  const b = valB[columnId];

  // For numbers (ascending/descending)
  if (!isNaN(a) && !isNaN(b)) {
    return desc ? b - a : a - b;
  }

  // For dates (ascending/descending)
  if (moment(a).isValid() && moment(b).isValid()) {
    return desc ? new Date(b) - new Date(a) : new Date(a) - new Date(b);
  }

  // For strings (lexicographical order)
  if (typeof a === 'string' && typeof b === 'string') {
    const data = desc ? b?.localeCompare(a) : a?.localeCompare(b); // Descending and Ascending order handling
    return data;
  }
  // Default (return 0 if no comparison can be made)
  return 0;
};



export function getMonths() {
  const months = [];
  for (let i = 1; i <= 12; i++) {
    const data = {
      value: (i).toString(),
      label: moment().month(i-1).format("MMMM"),
    }
    months.push(data);
  }
  return months;
}

export const getErrorMessageByCode = (code,type) => {
  if(type === 'leave-request'){
    return leaveRequestActionErrorCodes[code.toString()];
  }else if(type === 'remote-work'){
    return remoteWorkRequestActionErrorCodes[code.toString()];
  }else{
    return commonActionErrorCodes[code.toString()];
  }
};

export const getMonthDateRange = (month, year) => {
  const m = Number(month);
  const y = Number(year);

  const startDate = new Date(y, m - 1, 1);
  const endDate = new Date(y, m, 0);

  return {
    fromDate: moment(startDate).format(generalFormatDate),
    toDate: moment(endDate).format(generalFormatDate),
  };
};

export const TargetEmployeesEnum = {
  Company: 1,
  Employee: 2,
  Entity: 3,
  Location: 4,
  Area: 5,
  Group: 6,
  Category: 7,
  JobGrade: 8,
  Designation: 9,
};

export const FormFieldTypeEnum = {
  TextBox: 1,
  TextArea: 2,
  Number: 3,
  Date: 4,
  Time: 5,
  DropdownNumberRange: 6,
  DropdownCustom: 7,
  DropdownPredefined: 8,
  Radio: 9,
  Checkbox: 10,
  Switch: 11,
  FileUpload: 12,
  Signature: 13,
  MontlyLimitHoursMinutes: 14,
};

// Add dynamic bg color for dahboard chart
export const getCssVar = (name) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim();
 

const hexToRgb = (hex) => {
    const r = parseInt(hex.substring(1, 3), 16);
    const g = parseInt(hex.substring(3, 5), 16);
    const b = parseInt(hex.substring(5, 7), 16);
    return { r, g, b };
};

const rgbToHex = (r, g, b) => {
    r = Math.round(r).toString(16).padStart(2, '0');
    g = Math.round(g).toString(16).padStart(2, '0');
    b = Math.round(b).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
};

export const getRandomAvatarBgColor = (str, isLight) => {
    const safeStr = str == null ? '' : String(str);
    let hash = 0;
    if (safeStr.length === 0) {
        return isLight ? '#E5E7EB' : '#6B7280';
    }
    for (let i = 0; i < safeStr.length; i++) {
        hash = safeStr.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Generate base color
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xff;
        color += ('00' + value.toString(16)).substr(-2);
    }

    // Adjust lightness based on isLight parameter
    let { r, g, b } = hexToRgb(color);
    if (isLight) {
        // For lighter colors, blend with white
        const lightnessFactor = 0.7; // Example: 50% lighter
        r = r + (255 - r) * lightnessFactor;
        g = g + (255 - g) * lightnessFactor;
        b = b + (255 - b) * lightnessFactor;
    } else {
        // For darker colors, reduce proportionally
        const darknessFactor = 0.5; // Example: 50% darker
        r = r * darknessFactor;
        g = g * darknessFactor;
        b = b * darknessFactor;
    }
    
    return rgbToHex(r, g, b);
};

export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Convert Minutes to HH:MM String
export const minutesToHHMM = (minutes) => {
  if (minutes == null || isNaN(minutes)) return '00:00';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

// Convert HH:MM String to Total Minutes
// export const hhmmToMinutes = (hhmm) => {
//   if (!hhmm) return 0;
//   const [h, m] = hhmm.split(':').map(Number);
//   return h * 60 + m;
// };


export const hhmmToMinutes = (hhmm) => {
  if (!hhmm) return 0;
  const str = String(hhmm).trim();
  if (!str.includes(':')) return 0;

  const [h, m] = str.split(':').map(Number);
  return (isNaN(h) || isNaN(m)) ? 0 : h * 60 + m;
};

// convert total minutes → hhh:mm format (e.g. 125 → 002:05):
export const minutesToHHHMM = (minutes) => {
  if (isNaN(minutes) || minutes === null || minutes === undefined) return '000:00';
  
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;

  // Pad hours to 3 digits and minutes to 2 digits
  const paddedHrs = String(hrs).padStart(2, '0');
  const paddedMins = String(mins).padStart(2, '0');

  return `${paddedHrs}:${paddedMins}`;
};


export const convertToHHMM = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}${minutes}`;
};

export const convertDateToHH_MM = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const convertTimeToTodayDate= (timeStr)=> {
  if(!timeStr) return timeStr
  const [hours, minutes] = timeStr.split(':').map(Number);
  const now = new Date();
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0);
  return date;
}

export const convertTimeHHMMToTodayDate = (timeStr) => {
  if (!timeStr) return timeStr;
  const str = timeStr.toString().padStart(4, '0').replace(':', '');
  const [hours, minutes] = [str.slice(0, 2), str.slice(2, 4)].map(Number);
  const d = new Date(); 
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes);
};


export const getUnitDotColor = (unit) => {
  switch (String(unit).toLowerCase()) {
    case 'day': return '#C49221';
    case 'week': return '#ED097F';
    case 'month': return '#11B6A5';
    default: return 'gray';
  }
};

export const formatUnitText = (unit) => {
  if (!unit) return '-';
  const str = String(unit);
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const getNormalizedUnit = (unit) => {
  if (!unit) return 'month';
  const val = String(unit).toLowerCase();
  return val === 'day' ? 'days' : val;
};

export const getGeneratedDays = (unit, shiftTime, daysOfWeekList) => {
  if (unit === 'week') {
    return daysOfWeekList.map((day) => ({
      id: day.id,
      label: day.label,
      time: shiftTime,
      shifts: day.id % 2 === 0 ? 2 : null,
    }));
  }
  if (unit === 'days') {
    return Array.from({ length: 7 }, (_, i) => ({
      id: i + 1,
      label: `Day ${i + 1}`,
      time: shiftTime,
      shifts: i % 2 === 0 ? 2 : null,
    }));
  }
  return Array.from({ length: 31 }, (_, i) => ({
    id: i + 1,
    label: `Day ${i + 1}`,
    time: shiftTime,
    shifts: i % 3 === 0 ? 2 : i % 5 === 0 ? 4 : null,
  }));
};

// Utils or a new hook file
export function createPrivilegeMap(privList) {
  const map = new Map();
  (privList ?? []).forEach((row) => {
    const code = row?.menuDefinition?.menuCode;
    const action = row?.actionDefinition?.actionCode;
    const allowed = row?.isAllowed === true;
    if (!code || !action) return;
    if (!map.has(code)) map.set(code, new Set());
    if (allowed) map.get(code).add(action);
  });
  return map;
}

export function can(privilegeMap, menuCode, action) {
  return privilegeMap?.get(menuCode)?.has(action) === true;
}

export const canViewMenu = (privilegeMap, menuCode) => {
  return privilegeMap?.has(menuCode);
};

/**
 * Debounce function to limit the rate at which a function can fire
 * @param {Function} func - The function to debounce
 * @param {number} wait - The time in milliseconds to delay
 * @returns {Function} - The debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// FOR MENU CONFIGURATIONS
export const transformApiResponse = (apiData) => {
  const moduleMap = new Map();
  const memoizedResolve = new Map();
  const toKebabFromCode = (code) => {
    const raw = (code || '').toString().trim();
    if (!raw) return '/';

    // Convert CamelCase -> kebab-case, then normalize spaces/underscores
    const camel = raw.replace(/([a-z0-9])([A-Z])/g, '$1-$2');
    const normalized = camel
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();

    return `/${normalized}`;
  };

  for (let i = 0, len = (apiData?.length || 0); i < len; i++) {
    const priv = apiData[i];
    const def = priv?.menuDefinition;
    const actionName = priv?.actionDefinition?.name;
    const actionCode = priv?.actionDefinition?.actionCode;
    const moduleName = def?.moduleNameEn;
    const moduleNameAr = def?.moduleNameAr;
    const menuCode = def?.menuCode;
    if (!moduleName || !menuCode) continue;

    let mod = moduleMap.get(moduleName);
    if (!mod) {
      mod = { moduleName, moduleNameAr, menuItems: new Map() };
      moduleMap.set(moduleName, mod);
    }

    const key = `${moduleName}_${menuCode}`;
    const cfg = memoizedResolve.get(key) || resolveMenuConfig(moduleName, menuCode);
    if (!memoizedResolve.has(key)) memoizedResolve.set(key, cfg);

    const lcModule = (moduleName || '').toLowerCase();
    const lcCode = (menuCode || '').toLowerCase();
    const isLookups = lcCode === 'lookups' || lcCode === 'lookup';

    const apiMenuUrlRaw = def?.menuUrl;
    const apiMenuUrl = (typeof apiMenuUrlRaw === 'string' && apiMenuUrlRaw.trim())
      ? (apiMenuUrlRaw.trim().startsWith('/') ? apiMenuUrlRaw.trim() : `/${apiMenuUrlRaw.trim()}`)
      : null;

    const normalizedMenuRoute = (apiMenuUrl || '').toString().trim().toLowerCase().replace(/\/+$/, '');
    const isLookupRoute = normalizedMenuRoute === '/lookup';

    const fallbackPath = isLookups ? '/applicationconfigration' : (apiMenuUrl || toKebabFromCode(menuCode));

    const baseKey = (isLookups ? fallbackPath : (cfg?.path || fallbackPath)) || def.id;
    const itemKey = isLookups
      ? 'LOOKUPS:/applicationconfigration'
      : (isLookupRoute ? `${moduleName}|${baseKey}|${menuCode}` : `${moduleName}|${baseKey}`);

    if (!mod.menuItems.has(itemKey)) {
      mod.menuItems.set(itemKey, {
        id: itemKey,
        menuCode,
        menuNameEn: def.menuNameEn,
        menuNameAr: def.menuNameAr,
        name: def.name,
        sortOrder: def.sortOrder,
        menuOrder: def.menuOrder,
        showMenuInSideBar: isLookups ? true : (def?.showMenuInSideBar ?? def?.showInSideBar ?? true),
        actions: [],
        _actionSet: new Set(),

        path: isLookups ? fallbackPath : (cfg?.path || fallbackPath),
        icon: cfg?.icon || 'dashboard',
        titleEn: cfg?.titleEn || def.menuNameEn,
        titleAr: cfg?.titleAr || def.menuNameAr || def.menuNameEn,
      });
    }

    const menu = mod.menuItems.get(itemKey);
    const maybeAddAction = (val) => {
      const s = (val ?? '').toString().trim();
      if (!s) return;
      if (!menu._actionSet.has(s)) {
        menu._actionSet.add(s);
        menu.actions.push(s);
      }
    };

    maybeAddAction(actionName);
    // Include actionCode as well so callers can check configuration actions
    // (e.g., AZURECONFIGURATION / EMAILCONFIGURATION / MOBILECONFIGURATION)
    // while keeping backward compatibility for callers checking action names
    // like 'Add', 'Edit', 'Delete', etc.
    maybeAddAction(actionCode);
  }

  const result = [];
  moduleMap.forEach((mod) => {
    const items = Array.from(mod.menuItems.values()).map((it) => {
      if (it._actionSet) delete it._actionSet; // cleanup internal set
      return it;
    }).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    if (items.length > 0) result.push({ moduleName: mod.moduleName, moduleNameAr: mod.moduleNameAr, menuItems: items });
  });

  // Sort modules by menuOrder (from first menu item), ascending
  result.sort((a, b) => {
    const orderA = a.menuItems[0]?.menuOrder ?? 999;
    const orderB = b.menuItems[0]?.menuOrder ?? 999;
    if (orderA !== orderB) return orderA - orderB;
    return a.moduleName.localeCompare(b.moduleName);
  });

  return result;
};

export const savePageDefination = (apiData) => {
  try {
    const pages = [];
    const seen = new Set();

    (apiData || []).forEach((priv) => {
      const def = priv?.menuDefinition;
      if (!def?.id) return;

      const key = `${def.id}_${def.menuCode || ''}_${def.menuUrl || ''}`;
      if (seen.has(key)) return;
      seen.add(key);

      const menuUrl = def.menuUrl
        ? (String(def.menuUrl).startsWith('/') ? String(def.menuUrl) : `/${def.menuUrl}`)
        : '';

      pages.push({
        id: def.id,
        menuDefinitionID: priv?.menuDefinitionID || def.id,
        menuCode: def.menuCode,
        menuUrl,
        menuNameEn: def.menuNameEn || def.name || '',
        menuNameAr: def.menuNameAr || def.name || def.menuNameEn || '',
        name: def.name || def.menuNameEn || '',
        moduleNameEn: def.moduleNameEn || '',
        moduleNameAr: def.moduleNameAr || '',
        showMenuInSideBar: def.showMenuInSideBar,
      });
    });

    localStorage.setItem('pageDefination', JSON.stringify(pages));
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : savePageDefination -> error', error);
  }
};

export const getPageDefination = () => {
  try {
    const raw = localStorage.getItem('pageDefination');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : getPageDefination -> error', error);
    return [];
  }
};

export const getPageHeadingFromDefination = ({ pathname = '', menuCodes = [], fallbackEn = '', fallbackAr = '', isArabic = false } = {}) => {
  const pages = getPageDefination();
  const normalizedPath = String(pathname || '').toLowerCase().replace(/^\/+|\/+$/g, '');
  const normalizedCodes = (Array.isArray(menuCodes) ? menuCodes : [menuCodes])
    .filter(Boolean)
    .map((code) => String(code).toLowerCase());

  const pageByCode = normalizedCodes.length > 0
    ? pages.find((item) => normalizedCodes.includes(String(item?.menuCode || '').toLowerCase()))
    : null;

  let pageByPath = pages.find((item) => {
    const itemUrl = String(item?.menuUrl || '').toLowerCase().replace(/^\/+|\/+$/g, '');
    // Exact match first
    if (normalizedPath === itemUrl) {
      return true;
    }
    // Then check if path contains menuUrl or vice versa
    return (
      normalizedPath &&
      itemUrl &&
      (normalizedPath.includes(itemUrl) || itemUrl.includes(normalizedPath))
    );
  });

  // If no match found, try matching by removing common suffixes/prefixes
  if (!pageByPath) {
    const pathParts = normalizedPath.split('-');
    const pathBase = pathParts[pathParts.length - 1]; // Get last part (e.g., 'work' from 'hr-remote-work')
    
    pageByPath = pages.find((item) => {
      const itemUrl = String(item?.menuUrl || '').toLowerCase().replace(/^\/+|\/+$/g, '');
      if (!itemUrl) return false;
      
      // Try matching by base parts
      const itemUrlParts = itemUrl.split('-');
      const itemUrlBase = itemUrlParts[itemUrlParts.length - 1];
      
      // Check if the base parts match
      return normalizedPath.includes(itemUrlBase) || itemUrl.includes(pathBase);
    });
  }

  const page = pageByCode || pageByPath;

  if (!page) return isArabic ? (fallbackAr || fallbackEn) : (fallbackEn || fallbackAr);

  return isArabic
    ? (page.menuNameAr || page.menuNameEn || fallbackAr || fallbackEn)
    : (page.menuNameEn || page.menuNameAr || fallbackEn || fallbackAr);
};

export const getPermissionRequestErrorMessage = (errorCode, data, translate, fallback = '') => {
  const mapped = permissionRequestActionErrorCodes[errorCode];
  if (!mapped) return fallback;

  const values = {
    AllowedBefore:
      data?.AllowedBefore ?? data?.allowedBeforeDays ?? data?.allowedBefore ?? 0,
    AllowedAfter:
      data?.AllowedAfter ?? data?.allowedAfterDays ?? data?.allowedAfter ?? 0,
  };

  const translationKey = `permissionRequest.error.${mapped}`;
  const translated = translate(translationKey, values);
  return translated === translationKey ? mapped : translated;
};